import { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { Menu, Tooltip, Spin } from "antd";
import { LeftOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { useInvestigatorSitesQuery } from "@/hooks/useInvestigatorSitesQuery";
import { apiFetchJson } from "@/utils/apiFetch";
import type { Investigator } from "./types";
import InvestigatorTrialsTab from "./tabs/TrialsTab";
import InvestigatorMasteryTab from "./tabs/MasteryTab";
import SiteLocationsTab from "./SiteLocationsTab";
import NotesTab from "@/views/shared/NotesTab";

async function fetchPortfolioInvestigators(): Promise<Investigator[]> {
  return apiFetchJson<Investigator[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/portfolio-investigators/`,
  );
}

const DEFAULT_TAB = "trials";

function menuLabelWithCount(label: string, count?: number) {
  if (count == null) return label;
  return (
    <span className="flex items-center justify-between">
      {label}
      <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] rounded-full bg-[#E8EEF2] px-1.5 text-[11px] font-semibold text-[#48636F]">
        {count}
      </span>
    </span>
  );
}

function createMenuItems(
  trialCount?: number,
  siteCount?: number,
  notesHidden = false,
) {
  return [
    {
      type: "group" as const,
      label: "INVESTIGATOR",
      key: "group-investigator",
      children: [
        { key: "trials", label: menuLabelWithCount("Trials", trialCount) },
        {
          key: "site-locations",
          label: menuLabelWithCount("Site Locations", siteCount),
        },
        ...(notesHidden ? [] : [{ key: "notes", label: "Notes" }]),
        { key: "mastery", label: "Data Mastery" },
      ],
    },
  ];
}

export default function InvestigatorProfile() {
  const { invKey, tab } = useParams<{ invKey: string; tab?: string }>();
  const { scope, id, buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const activeTab = tab ?? DEFAULT_TAB;
  const notesHidden = useFeatureFlag(FEATURE_FLAGS.NOTES_HIDDEN);

  const { data: allInvestigators = [], isLoading } = useQuery({
    queryKey: ["portfolio-investigators", scope, id],
    queryFn: () => fetchPortfolioInvestigators(),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const investigator = useMemo(
    () => allInvestigators.find((i) => i.inv_key === invKey) ?? null,
    [allInvestigators, invKey],
  );

  const trialIds = useMemo(
    () =>
      investigator
        ? [...new Set((investigator.trials ?? []).map((t) => t.trial_id))]
        : [],
    [investigator],
  );

  const { data: investigatorSites } = useInvestigatorSitesQuery(invKey);

  const menuItems = useMemo(
    () =>
      createMenuItems(trialIds.length, investigatorSites?.length, notesHidden),
    [trialIds.length, investigatorSites?.length, notesHidden],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin />
      </div>
    );
  }

  if (!investigator) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-[13px]">
        Investigator not found.
      </div>
    );
  }

  const handleMenuSelect = (key: string) => {
    navigate(buildUrl(`investigators/${invKey}/${key}`));
  };

  const renderContent = () => {
    switch (activeTab) {
      case "trials":
        return (
          <InvestigatorTrialsTab
            trialEntries={investigator.trials ?? []}
            buildUrl={buildUrl}
          />
        );
      case "site-locations":
        return <SiteLocationsTab invKey={invKey!} />;
      case "mastery":
        return <InvestigatorMasteryTab investigator={investigator} />;
      case "notes":
        return notesHidden ? null : (
          <NotesTab
            entityKey={`inv:${investigator.inv_key}`}
            trialOptions={trialIds.map((id) => ({ value: id, label: id }))}
          />
        );
      default:
        return (
          <InvestigatorTrialsTab
            trialEntries={investigator.trials ?? []}
            buildUrl={buildUrl}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Link
        to={buildUrl("investigators")}
        className="flex items-center gap-1.5 text-[12px] text-[#556C75] hover:text-[#2B4752] px-4 py-2 shrink-0"
      >
        <LeftOutlined className="text-[10px]" />
        All Investigators
      </Link>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-[260px] min-w-[260px] sticky top-0 self-start h-full flex flex-col gap-2 px-3 pt-4 pb-6 overflow-y-auto">
          {/* Investigator info card */}
          <div
            className="px-3 py-3 flex flex-col gap-1"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-[#E8EEF2] flex items-center justify-center text-[13px] font-semibold text-[#2B4752]">
                {investigator.name.split(" ").slice(-1)[0][0]}
              </div>
              <span className="text-[11px] font-medium uppercase tracking-wide text-[#8C8C8C]">
                Investigator
              </span>
            </div>

            <span className="text-[14px] font-semibold text-[#1F2933] leading-snug">
              {investigator.name}
            </span>
            {investigator.degree && (
              <span className="text-[12px] text-[#6B7280]">
                {investigator.degree}
              </span>
            )}
            {investigator.specialty && (
              <span className="text-[12px] text-[#556C75] mt-0.5">
                {investigator.specialty}
              </span>
            )}
            {investigator.institution && (
              <span className="text-[11px] text-[#8C8C8C] italic">
                {investigator.institution}
              </span>
            )}

            <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-[#F0F2F4]">
              {investigator.overall_confidence != null && (
                <Tooltip title="Entity match confidence from vivo-core mastering">
                  <div className="cursor-help">
                    <div
                      className="text-[16px] font-semibold"
                      style={{
                        color:
                          investigator.overall_confidence >= 0.9
                            ? "#22C55E"
                            : investigator.overall_confidence >= 0.7
                              ? "#F59E0B"
                              : "#EF4444",
                      }}
                    >
                      {Math.round(investigator.overall_confidence * 100)}%
                    </div>
                    <div className="text-[11px] text-[#8C8C8C] uppercase tracking-wide">
                      Confidence
                    </div>
                  </div>
                </Tooltip>
              )}
              {investigator.is_kol && (
                <Tooltip title="Key Opinion Leader">
                  <div className="flex items-center gap-1 cursor-help">
                    <CheckCircleFilled
                      style={{ color: "#4CAF50", fontSize: 12 }}
                    />
                    <div className="text-[11px] font-semibold text-[#22C55E] uppercase tracking-wide">
                      KOL
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Nav menu */}
          <div
            className="overflow-y-auto overflow-clip"
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0, 0, 0, 0.04)",
              background: "#FFF",
              boxShadow: "0 2px 15px 4px rgba(0, 0, 0, 0.02)",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[activeTab]}
              onSelect={({ key }) => handleMenuSelect(key)}
              items={menuItems}
              style={{ borderRight: "none", background: "transparent" }}
            />
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 flex flex-col overflow-y-auto">
          <div className="flex-1 p-4">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
